import { useRef, useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { MailOutline as MailIcon, ContentCopy as CopyIcon, Check as CheckIcon } from "@mui/icons-material";
import { tokens } from "../../theme";

/** Cuánto dura la confirmación en el botón antes de volver a su estado normal. */
const AVISO_MS = 2000;

/**
 * Correo de una persona, pensado para llevárselo.
 *
 * Suelto en una línea de datos es incómodo de copiar: hay que arrastrar sobre
 * unos pocos píxeles sin pasarse al dato de al lado. Aquí va en su propia caja,
 * un clic encima lo selecciona entero (`user-select: all`) y el botón lo copia
 * sin seleccionar nada.
 *
 * La confirmación vive en el propio botón —el ícono pasa a un ✓ y el tooltip lo
 * dice—, donde está mirando quien acaba de pulsarlo.
 *
 * Si el navegador niega el portapapeles (sin HTTPS, o permiso denegado), el
 * botón deja la dirección seleccionada: así siempre hace algo y basta con Ctrl+C.
 *
 * Dentro de una fila clicable, copiar o seleccionar el correo no debe además
 * abrir la ficha: el componente se queda con el clic y con la tecla.
 */
export default function Correo({ correo }: { correo: string }) {
  const [estado, setEstado] = useState<"" | "copiado" | "seleccionado">("");
  const texto = useRef<HTMLElement>(null);

  const seleccionar = () => {
    const nodo = texto.current;
    const seleccion = window.getSelection();
    if (!nodo || !seleccion) return;
    const rango = document.createRange();
    rango.selectNodeContents(nodo);
    seleccion.removeAllRanges();
    seleccion.addRange(rango);
  };

  const avisar = (v: "copiado" | "seleccionado") => {
    setEstado(v);
    setTimeout(() => setEstado(""), AVISO_MS);
  };

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(correo);
      avisar("copiado");
    } catch {
      seleccionar();
      avisar("seleccionado");
    }
  };

  const titulo = estado === "copiado" ? "Copiado" : estado === "seleccionado" ? "Seleccionado: cópialo con Ctrl+C" : "Copiar correo";

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        bgcolor: tokens.etiqueta.neutra.bg,
        color: tokens.etiqueta.neutra.fg,
        borderRadius: `${tokens.boton.radio}px`,
        padding: "3px 3px 3px 10px",
        maxWidth: "100%",
      }}
    >
      <MailIcon sx={{ fontSize: 16, flexShrink: 0 }} />
      <Box component="span" ref={texto} sx={{ fontSize: 14, userSelect: "all", overflowWrap: "anywhere", minWidth: 0 }}>
        {correo}
      </Box>
      <Tooltip title={titulo}>
        <IconButton
          size="small"
          onClick={copiar}
          aria-label={`Copiar el correo ${correo}`}
          sx={{ color: "inherit", p: 0.5, flexShrink: 0 }}
        >
          {estado === "copiado" ? <CheckIcon sx={{ fontSize: 16 }} /> : <CopyIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </Tooltip>
    </Box>
  );
}
